import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { ContentHeader } from "@cx/ui";
import type { DailyDashboardResult } from "@cx/contracts";
import { formatDateBR } from "@cx/domain";
import { useSession } from "../../session";
import { ChartCard } from "./ChartCard";
import { count, hourLabel, money, reportQuery } from "./labels";
import { statusList } from "./search";
import {
  FilterRow,
  ReportStatus,
  SettingsButton,
  StatusFilter,
  reportQueryOptions,
  useDashboardOptions,
} from "./shared";
import { DreTable, SalesKpis } from "./widgets";
import { CompanyTabs } from "./CompanyTabs";

const PATH = "/meu-negocio/diario";

/** Dashboard Diário (RF-BI-07): o dia hora a hora contra o mesmo dia da semana anterior (ou o dia escolhido). */
export function DailyPage(): ReactElement {
  const { companyId, api } = useSession();
  const search = useSearch({ from: PATH });
  const navigate = useNavigate({ from: PATH });
  const options = useDashboardOptions();
  const statuses = statusList(search.statuses);
  const set = (patch: Partial<typeof search>) =>
    navigate({ search: (s) => ({ ...s, ...patch }), replace: true });
  const d = useQuery({
    ...reportQueryOptions,
    queryKey: ["dashboard", companyId, "daily", search.date, search.compareDate, search.statuses],
    queryFn: () =>
      api<DailyDashboardResult>(
        `/reports/daily${reportQuery({ date: search.date, compareDate: search.compareDate, statuses })}`,
      ),
  });
  const r = d.data;
  return (
    <>
      <ContentHeader title="Diário" breadcrumb={["Meu Negócio", "Diário"]} actions={<SettingsButton />} />
      <CompanyTabs />
      <FilterRow>
        <div className="col-6 col-md-2">
          <label className="form-label small" htmlFor="daily-date">
            Dia
          </label>
          <input
            id="daily-date"
            type="date"
            className="form-control"
            value={search.date ?? r?.date ?? ""}
            onChange={(e) => set({ date: e.target.value || undefined })}
          />
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label small" htmlFor="daily-compare">
            Comparar com
          </label>
          <input
            id="daily-compare"
            type="date"
            className="form-control"
            value={search.compareDate ?? r?.compareDate ?? ""}
            onChange={(e) => set({ compareDate: e.target.value || undefined })}
          />
        </div>
        <div className="col-12 col-md-3">
          {options.data && (
            <StatusFilter
              statuses={options.data.statuses}
              defaults={options.data.settings.saleStatusIds}
              value={statuses}
              onChange={(ids) => set({ statuses: ids?.join(",") })}
            />
          )}
        </div>
        <div className="col-12 col-md-auto">
          <button
            type="button"
            className="btn btn-link"
            onClick={() => navigate({ search: {}, replace: true })}
          >
            Hoje
          </button>
        </div>
      </FilterRow>
      <ReportStatus loading={d.isLoading} error={d.error} />
      {r && (
        <>
          <p className="text-muted small">
            {formatDateBR(r.date)} comparado com {formatDateBR(r.compareDate)}
          </p>
          <SalesKpis cards={r.cards} />
          <div className="mb-3">
            <ChartCard
              title="Valor de pedidos por hora"
              type="bar"
              categories={r.hours.map((h) => hourLabel(h.hour))}
              series={[
                { name: formatDateBR(r.date), data: r.hours.map((h) => h.current.totalCents) },
                { name: formatDateBR(r.compareDate), data: r.hours.map((h) => h.previous.totalCents) },
              ]}
              unit="money"
              filename={`valor-por-hora-${r.date}`}
              testId="chart-hours"
            />
            {!!(r.noHour.current.orders || r.noHour.previous.orders) && (
              <p className="small text-muted mt-2" data-testid="no-hour">
                Pedidos lançados com data retroativa (sem horário): {count(r.noHour.current.orders)} no dia (
                {money(r.noHour.current.totalCents)}) e {count(r.noHour.previous.orders)} na comparação (
                {money(r.noHour.previous.totalCents)}).
              </p>
            )}
          </div>
          <div className="row g-3 mb-3">
            <div className="col-xl-6">
              <DreTable dre={r.dre} />
            </div>
          </div>
        </>
      )}
    </>
  );
}
